import { useCallback, useRef, useState } from "react";
import { Text, Button, Spinner } from "@radix-ui/themes";
import { ImageIcon, PlusIcon } from "@radix-ui/react-icons";
import Switch from "@/components/shared/inputs/switch";
import { UploadQuackchatAssestResponse } from "@/components/shared/upload-file";
import styles from "./MinimalUpload.module.scss";

interface MinimalUploadProps {
  label: string;
  enabled?: boolean;
  onEnabledChange: (enabled: boolean) => void;
  fileSrc?: string;
  onFileUpload: (file: File) => Promise<UploadQuackchatAssestResponse>;
  onSuccess: (filePath: string) => void;
}

export const MinimalUpload = ({
  label,
  enabled = true,
  onEnabledChange,
  fileSrc,
  onFileUpload,
  onSuccess,
}: MinimalUploadProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      try {
        setLoading(true);
        setError(undefined);
        const response = await onFileUpload(file);
        onSuccess(response.path);
      } catch (err) {
        console.error(err);
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("Failed to upload");
        }
      } finally {
        setLoading(false);
        if (inputRef.current) {
          inputRef.current.value = "";
        }
      }
    },
    [onFileUpload, onSuccess]
  );

  const handleButtonClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text size="2" weight="medium">
          {label}
        </Text>
        <Switch value={enabled} onChange={onEnabledChange} />
      </div>

      {enabled && (
        <div className={styles.uploadArea}>
          <div className={styles.previewCircle}>
            {fileSrc ? (
              <img
                src={fileSrc}
                alt={`${label} preview`}
                className={styles.previewImage}
              />
            ) : (
              <div className={styles.placeholder}>
                <ImageIcon width="24" height="24" />
              </div>
            )}
          </div>

          <Button
            variant="outline"
            color="gray"
            size="2"
            onClick={handleButtonClick}
            disabled={loading}
            className={styles.uploadButton}
          >
            {loading ? (
              <Spinner loading>
                <PlusIcon />
              </Spinner>
            ) : (
              <>
                <PlusIcon />
                Add {label}
              </>
            )}
          </Button>

          {error && (
            <Text size="1" color="red" className={styles.errorText}>
              {error}
            </Text>
          )}

          <input
            type="file"
            ref={inputRef}
            onChange={handleFileChange}
            accept="image/png, image/jpeg, image/jpg, image/webp"
            style={{ display: "none" }}
          />
        </div>
      )}
    </div>
  );
};
